/**
 * BM-H347 — fixed-fee narrative template picker.
 *
 * Renders the template library held in
 * ../data/fixedFeeNarrativeTemplates.json. This component holds NO
 * wording of its own: the narratives are the firm's own issued-invoice
 * wording and live in that one data file. Editing the wording is a data
 * edit, not a code change.
 *
 * Behaviour:
 *   * Appears only where the caller renders it — the caller gates on
 *     wip_or_fixed === "Fixed fee". No gating logic here.
 *   * Filters by the matter's transaction type when one is known, by
 *     membership test against each template's transaction_types array.
 *     No branching on the type value. When no type is known, or the
 *     known type matches nothing, every template is offered rather than
 *     none — a fee earner with an unclassified matter still needs the
 *     library.
 *   * Shows the FULL narrative text before selection, so the fee earner
 *     is choosing wording rather than choosing a code.
 *   * On "Use this wording" it writes the narrative into the existing
 *     fixed_fee_narrative field and the template code into
 *     fixed_fee_narrative_template_code. The field stays ordinary
 *     editable text afterwards; this component does not own it.
 *
 * The invoice itself is produced in LawBillity. Nothing here renders,
 * prices, or totals anything.
 */
import { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { ChevronDown, ChevronUp, FileText } from "lucide-react";
import templateLibrary from "../data/fixedFeeNarrativeTemplates.json";

export const FIXED_FEE_NARRATIVE_TEMPLATES = templateLibrary.templates;

export const NARRATIVE_FIELD = "fixed_fee_narrative";
export const TEMPLATE_CODE_FIELD = "fixed_fee_narrative_template_code";

const BILLING_STAGE_LABELS = {
  first_invoice: "First invoice",
  completion: "On completion",
  single: "Single invoice",
};

/**
 * Templates applicable to a transaction type. Pure membership test on
 * data; falls back to the whole library when the type is unknown or
 * matches nothing.
 */
export function templatesForTransactionType(transactionType) {
  const key = (transactionType || "").trim();
  if (!key) return FIXED_FEE_NARRATIVE_TEMPLATES;
  const matches = FIXED_FEE_NARRATIVE_TEMPLATES.filter((t) =>
    (t.transaction_types || []).includes(key)
  );
  return matches.length ? matches : FIXED_FEE_NARRATIVE_TEMPLATES;
}

export default function FixedFeeNarrativePicker({
  transactionType,
  selectedCode,
  onSelect,
}) {
  const [open, setOpen] = useState(false);

  const templates = useMemo(
    () => templatesForTransactionType(transactionType),
    [transactionType]
  );
  const filtered = templates.length !== FIXED_FEE_NARRATIVE_TEMPLATES.length;

  const selected = FIXED_FEE_NARRATIVE_TEMPLATES.find(
    (t) => t.code === selectedCode
  );

  return (
    <div
      className="rounded-md border border-teal/40 bg-white/70"
      data-testid="fixed-fee-narrative-picker"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        data-testid="fixed-fee-narrative-picker-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-teal">
          <FileText className="h-4 w-4" />
          Use a standard narrative
          <span className="text-xs font-normal text-slate-500">
            ({templates.length} available
            {filtered ? " for this transaction type" : ""})
          </span>
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-slate-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-slate-400" />
        )}
      </button>

      {selected && (
        <p
          className="px-3 pb-2 text-xs text-slate-500"
          data-testid="fixed-fee-narrative-picker-selected"
        >
          Using <span className="font-mono">{selected.code}</span> — you can
          edit the wording below.
        </p>
      )}

      {open && (
        <div className="px-3 pb-3 space-y-3">
          <p className="text-xs text-slate-500">
            Wording taken from the firm's issued invoices. Pick one, then edit
            it in the box below if this matter needs different wording.
          </p>
          {templates.map((t) => (
            <div
              key={t.code}
              className="rounded-md border border-slate-200 p-3"
              data-testid={`fixed-fee-narrative-template-${t.code}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-sm font-medium text-slate-700">
                    {t.label}
                  </Label>
                  <p className="text-xs text-slate-400 font-mono mt-0.5">
                    {t.code}
                    {BILLING_STAGE_LABELS[t.billing_stage]
                      ? ` · ${BILLING_STAGE_LABELS[t.billing_stage]}`
                      : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={t.code === selectedCode ? "secondary" : "outline"}
                  onClick={() => {
                    if (onSelect) onSelect(t);
                    setOpen(false);
                  }}
                  data-testid={`fixed-fee-narrative-use-${t.code}`}
                >
                  {t.code === selectedCode ? "Selected" : "Use this wording"}
                </Button>
              </div>
              {/* Full text before selection — not a preview, not truncated. */}
              <p
                className="mt-2 text-xs leading-relaxed text-slate-600 whitespace-pre-wrap"
                data-testid={`fixed-fee-narrative-text-${t.code}`}
              >
                {t.narrative}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
